import React, { useState } from 'react';
import { Wrench, Sunglasses, Sparkle, ShieldCheck, UserCircle } from '@phosphor-icons/react';
import { StatusSelect, LocationSelect, BrandSelect, TextEdit, DealSelect, KeysSelect, SalespersonSelect } from './EditableCell';
import { formatDate, dayOfWeek, todayISO, COLOUR_MAP, canEditField } from '../helpers';
import { api } from '../api';
import { showToast } from './Toast';

/**
 * Mobile-friendly card for one vehicle — replaces the 13-column table row on
 * small screens. Same inline editing (optimistic save) as VehicleRow.
 */
export default function VehicleCard({ vehicle, onUpdate, onChanged, role }) {
  const v = vehicle;
  const [local, setLocal] = useState(v);
  React.useEffect(() => { setLocal(v); }, [v]);

  const can = (field) => canEditField(role, field);
  const isToday = local.delivery_date && String(local.delivery_date).slice(0, 10) === todayISO();

  async function save(field, value) {
    if (!can(field)) {
      showToast('You do not have permission to edit this field');
      return;
    }
    const prev = local[field];
    setLocal((s) => ({ ...s, [field]: value }));
    try {
      await api.updateVehicle(v.id, { [field]: value });
      onChanged?.();
    } catch (e) {
      setLocal((s) => ({ ...s, [field]: prev }));
      showToast('Failed: ' + e.message);
    }
  }

  return (
    <div className={`vcard ${isToday ? 'vcard-today' : ''}`}>
      {/* Header: brand logo · model/stock · delivery (mirrors the Acts queue row) */}
      <div className="vcard-head" onClick={() => onUpdate(v.id)}>
        <BrandSelect value={local.brand} onChange={(val) => save('brand', val)} disabled={!can('brand')} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="vm">{local.model}</div>
          <div className="vs">{local.stock_number}{local.ro_number ? ' · ' + local.ro_number : ''}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {local.delivery_date ? (
            <>
              <div className="dc-time" style={isToday ? { color: 'var(--amber)', fontWeight: 800 } : null}>
                {local.delivery_time || '—'}
              </div>
              <div className="dc-date">{formatDate(local.delivery_date)} · {dayOfWeek(local.delivery_date)}</div>
              {isToday && <span className="today-pill">TODAY</span>}
            </>
          ) : (
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>No delivery date</span>
          )}
        </div>
      </div>

      {/* Badges row: deal + location + colour */}
      <div className="vcard-row">
        <DealSelect     value={local.deal_status} onChange={(val) => save('deal_status', val)} disabled={!can('deal_status')} />
        <LocationSelect value={local.location} onChange={(val) => save('location', val)} disabled={!can('location')} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {local.colour && <span className="cdot" style={{ background: COLOUR_MAP[local.colour] || '#ccc' }} />}
          <TextEdit value={local.colour} placeholder="Colour" onChange={(val) => save('colour', val)} disabled={!can('colour')} />
        </span>
        <span className="frow-sales" title={local.salesperson || 'Salesperson'}>
          <UserCircle size={13} weight="duotone" />
          <SalespersonSelect
            value={local.salesperson}
            onChange={(val) => save('salesperson', val)}
            disabled={!can('salesperson')}
            className="sales-pick"
          />
        </span>
      </div>

      {/* Status grid */}
      <div className="vcard-grid">
        <div className="vcard-cell">
          <span className="vcard-lbl"><Wrench size={13} weight="duotone" /> Workshop</span>
          <StatusSelect value={local.status_workshop} onChange={(val) => save('status_workshop', val)} disabled={!can('status_workshop')} />
        </div>
        <div className="vcard-cell">
          <span className="vcard-lbl"><Sunglasses size={13} weight="duotone" /> Tint</span>
          <StatusSelect value={local.status_tint} onChange={(val) => save('status_tint', val)} disabled={!can('status_tint')} />
        </div>
        <div className="vcard-cell">
          <span className="vcard-lbl"><Sparkle size={13} weight="duotone" /> Detail</span>
          <StatusSelect value={local.status_detail} onChange={(val) => save('status_detail', val)} disabled={!can('status_detail')} />
        </div>
        <div className="vcard-cell">
          <span className="vcard-lbl"><ShieldCheck size={13} weight="duotone" /> Paint Pack</span>
          <StatusSelect value={local.status_paint} onChange={(val) => save('status_paint', val)} disabled={!can('status_paint')} />
        </div>
        <div className="vcard-cell" style={{ gridColumn: '1 / -1' }}>
          <span className="vcard-lbl">Keys</span>
          <KeysSelect
            value={local.keys_status}
            onChange={(val) => save('keys_status', val)}
            disabled={!can('keys_status')}
          />
        </div>
      </div>

      {/* Notes + action */}
      <div className="vcard-foot">
        <div style={{ flex: 1, fontSize: 11, color: 'var(--muted)', minWidth: 0 }}>
          <TextEdit value={local.notes} placeholder="Add notes…" onChange={(val) => save('notes', val)} disabled={!can('notes')} />
        </div>
        <button className="ab p" onClick={() => onUpdate(v.id)}>
          {can('brand') ? 'Update' : 'View'}
        </button>
      </div>
    </div>
  );
}
